import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import {
  findFuglMeyerLockedItems,
  FUGL_MEYER_PART_IV_ITEMS,
  FUGL_MEYER_PART_V_ITEM,
} from 'src/app/utils/questionnaire-locked-items';

interface FuglMeyerScoreField {
  controlName: string;
  maxScore: number;
  isTotal: boolean;
  // The answers it adds up, or for a total the score fields it adds up.
  parts: string[];
}

// Same pattern the backend uses. The points are the number in "(n)", never
// the word ("(0) none" and "(2) none" both exist) or the choice position.
const FUGL_MEYER_POINTS_REGEX = /^\s*\(\s*([0-2])\s*\)/;

// Titles separate the instruction with "||"; some may use "‖" instead.
export const FUGL_MEYER_TITLE_SEPARATOR = /\|\||‖/;

// Sub-headings that label a group of items, as on the official form
// (flexor synergy, extensor synergy, grasp); the other sections are parts.
const FUGL_MEYER_GROUP_LABELS = ['question722', 'question729', 'question756'];

// The part IV and part V subtotals: part V only counts when part IV is full.
const FUGL_MEYER_PART_IV_CONTROL_NAME = 'question743';
const FUGL_MEYER_PART_V_CONTROL_NAME = 'question746';

// In calculation order: a total comes after the fields it adds up.
const FUGL_MEYER_SCORE_FIELDS: FuglMeyerScoreField[] = [
  // A. Upper extremity: subtotals I to V
  {
    controlName: 'question720',
    maxScore: 4,
    isTotal: false,
    parts: ['question718', 'question719'],
  },
  {
    controlName: 'question733',
    maxScore: 18,
    isTotal: false,
    parts: [
      'question723',
      'question724',
      'question725',
      'question726',
      'question727',
      'question728',
      'question730',
      'question731',
      'question732',
    ],
  },
  {
    controlName: 'question738',
    maxScore: 6,
    isTotal: false,
    parts: ['question735', 'question736', 'question737'],
  },
  {
    controlName: 'question743',
    maxScore: 6,
    isTotal: false,
    parts: FUGL_MEYER_PART_IV_ITEMS,
  },
  {
    controlName: 'question746',
    maxScore: 2,
    isTotal: false,
    parts: [FUGL_MEYER_PART_V_ITEM],
  },
  // Total A
  {
    controlName: 'question747',
    maxScore: 36,
    isTotal: true,
    parts: [
      'question720',
      'question733',
      'question738',
      'question743',
      'question746',
    ],
  },
  // Total B. Wrist
  {
    controlName: 'question753',
    maxScore: 10,
    isTotal: true,
    parts: [
      'question748',
      'question749',
      'question750',
      'question751',
      'question752',
    ],
  },
  // Total C. Hand
  {
    controlName: 'question762',
    maxScore: 14,
    isTotal: true,
    parts: [
      'question754',
      'question755',
      'question757',
      'question758',
      'question759',
      'question760',
      'question761',
    ],
  },
  // Total D. Coordination/speed
  {
    controlName: 'question766',
    maxScore: 6,
    isTotal: true,
    parts: ['question763', 'question764', 'question765'],
  },
  // TOTAL A-D, the same number as the backend score
  {
    controlName: 'question767',
    maxScore: 66,
    isTotal: true,
    parts: ['question747', 'question753', 'question762', 'question766'],
  },
  // Total H. Sensation
  {
    controlName: 'question774',
    maxScore: 12,
    isTotal: true,
    parts: [
      'question768',
      'question769',
      'question770',
      'question771',
      'question772',
      'question773',
    ],
  },
  // Total J. Passive joint motion
  {
    controlName: 'question787',
    maxScore: 24,
    isTotal: true,
    parts: [
      'question775',
      'question776',
      'question777',
      'question778',
      'question779',
      'question780',
      'question781',
      'question782',
      'question783',
      'question784',
      'question785',
      'question786',
    ],
  },
  // Total J. Joint pain
  {
    controlName: 'question801',
    maxScore: 24,
    isTotal: true,
    parts: [
      'question789',
      'question790',
      'question791',
      'question792',
      'question793',
      'question794',
      'question795',
      'question796',
      'question797',
      'question798',
      'question799',
      'question800',
    ],
  },
];

@Component({
  selector: 'app-fugl-meyer-questions',
  templateUrl: './fugl-meyer-questions.component.html',
  styleUrl: './fugl-meyer-questions.component.css',
  standalone: false,
})
export class FuglMeyerQuestionsComponent implements OnInit, OnDestroy {
  // The current page, which is the one shown.
  @Input() elements: IQuestionnaireQuestion[] = [];
  // Every page: the totals add up answers from other pages.
  @Input() allElements: IQuestionnaireQuestion[] = [];
  // The current page's title, with its instruction after the separator.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateScores();
    });
    this.recalculateScores();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(FUGL_MEYER_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(FUGL_MEYER_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isGroupLabel(element: IQuestionnaireQuestion): boolean {
    return FUGL_MEYER_GROUP_LABELS.includes(element.name);
  }

  // Part V waits for a full part IV, so it is shown dimmed until then.
  isWaitingForPartIV(element: IQuestionnaireQuestion): boolean {
    return element.name === FUGL_MEYER_PART_V_ITEM && this.isPartVLocked();
  }

  isScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && !!this.getScoreField(element.name);
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return this.getScoreField(element.name)?.isTotal ?? false;
  }

  // The template has no maximums, so the UI supplies them.
  getMaxScore(controlName: string): number | null {
    return this.getScoreField(controlName)?.maxScore ?? null;
  }

  getScoreDisplay(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  private getScoreField(controlName: string): FuglMeyerScoreField | undefined {
    return FUGL_MEYER_SCORE_FIELDS.find(
      (field) => field.controlName === controlName,
    );
  }

  private recalculateScores(): void {
    const scores = new Map<string, number | null>();

    for (const field of FUGL_MEYER_SCORE_FIELDS) {
      const score =
        field.controlName === FUGL_MEYER_PART_V_CONTROL_NAME
          ? this.calculatePartV(
              scores.get(FUGL_MEYER_PART_IV_CONTROL_NAME) ?? null,
            )
          : this.sumParts(field.parts, scores);

      scores.set(field.controlName, score);
      this.setLockedScore(field.controlName, score);
    }

    this.cdr.detectChanges();
  }

  // Live sum of the parts answered so far, or null while none is, so an
  // untouched part stays empty and is not saved.
  private sumParts(
    parts: string[],
    scores: Map<string, number | null>,
  ): number | null {
    const points = parts
      .map((part) =>
        scores.has(part)
          ? (scores.get(part) ?? null)
          : this.getSelectedPoints(part),
      )
      .filter((point): point is number => point !== null);

    return points.length ? points.reduce((sum, point) => sum + point, 0) : null;
  }

  // Part V only counts when part IV is full. Until then question745 is
  // locked and subtotal V is 0; an earlier answer is kept in the form, but it
  // is not counted or saved.
  private calculatePartV(partIV: number | null): number | null {
    const question = this.formGroup.get(FUGL_MEYER_PART_V_ITEM);

    if (this.isPartVLocked()) {
      question?.disable({ emitEvent: false });
      return partIV === null ? null : 0;
    }

    // Unlocked only when the part IV answers can be edited too.
    const partIVEditable = this.getScoreField(
      FUGL_MEYER_PART_IV_CONTROL_NAME,
    )?.parts.some((part) => this.formGroup.get(part)?.enabled);
    if (partIVEditable) {
      question?.enable({ emitEvent: false });
    }

    return this.getSelectedPoints(FUGL_MEYER_PART_V_ITEM);
  }

  // The backend's rule: until part IV is full, part V scores 0 without an
  // answer.
  private isPartVLocked(): boolean {
    return findFuglMeyerLockedItems(this.allElements, (item) =>
      this.formGroup.get(item)?.value,
    ).has(FUGL_MEYER_PART_V_ITEM);
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.allElements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(FUGL_MEYER_POINTS_REGEX);

    return match ? Number(match[1]) : null;
  }

  private setLockedScore(controlName: string, score: number | null): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    control.setValue(score === null ? '' : String(score), {
      emitEvent: false,
    });
    control.disable({ emitEvent: false });
  }
}
